// Service hook (TS-2.5.01, Resource Accessor layer) - the only place the decks feature's version
// detail screen calls publicationApi.getVersion. `versionKey` of null means "nothing selected yet"
// and skips the fetch entirely, the same optional-key contract useProjectDetail uses for "new".
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { DeckVersionDetail } from "../../../api/generated/contracts";
import { publicationApi } from "../../../api/publication";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseDeckVersionDetailResult {
  readonly detail: DeckVersionDetail | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
}

export function useDeckVersionDetail(versionKey: string | null): UseDeckVersionDetailResult {
  const [detail, setDetail] = useState<DeckVersionDetail | null>(null);
  const [loading, setLoading] = useState(versionKey !== null);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!versionKey) {
      setDetail(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    void publicationApi
      .getVersion(versionKey)
      .then((next) => {
        setDetail(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The version could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [versionKey]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, "DeckVersion");

  return { detail, loading, error, refresh };
}
